/**
 * 建單時的「輸入編號 → 自動帶入主檔」(REQ-20260806002)。
 *
 * 需求的第一條訴求就是免重複打字:會計在出貨 / 進貨 / 應收 / 應付四張單上
 * 只要打客戶或廠商編號,統編、送貨地址、電話就要自己跳出來。
 * 四個模組共用同一組型別,避免各自定義出不一樣的欄位名。
 */
import { z } from 'zod';

export const PartyLookupQuerySchema = z
  .object({
    type: z.enum(['customer', 'supplier']),
    /**
     * 主檔 id。別的畫面已經知道是哪個客戶(例:訪談頁的收款深連結,REQ-20260815001),
     * 不必再用關鍵字猜一次 —— 帶 id 時直接回那一筆,不看 keyword。
     */
    id: z.coerce.number().int().positive().optional(),
    /** 編號(customers.cCode / suppliers.code)或名稱關鍵字 */
    keyword: z.string().trim().min(1, '請輸入編號或名稱').optional(),
  })
  .refine((v) => v.id != null || v.keyword != null, {
    message: '請輸入編號或名稱',
    path: ['keyword'],
  });
export type PartyLookupQuery = z.infer<typeof PartyLookupQuerySchema>;

/** 出貨 / 進貨單建立時要帶入的主檔快照 */
export interface PartyInfo {
  id: number;
  type: 'customer' | 'supplier';
  code: string | null;
  fullName: string;
  simpleName: string;
  /** 統一編號 */
  taxId: string | null;
  /** 送貨 / 收貨地址 */
  address: string | null;
  /** 發票地址(客戶才有) */
  addressInvoice: string | null;
  contact: string | null;
  /** 主要電話:優先取手機,沒有才取電話清單第一筆 */
  phone: string | null;
  phones: Array<{ phoneType: string; number: string; note: string | null }>;
  email: string | null;
  /** 客戶才有:結帳週期 / 付款憑證(發票選單) */
  settlementCycle: string | null;
  settlementCycleLabel: string | null;
  settlementDay: number | null;
  paymentCert: string | null;
  paymentMethod: string | null;
  /**
   * 廠商才有:主檔的「稅額」(INCLUSIVE / EXCLUSIVE / EXEMPT),客戶恆為 null。
   * 純顯示用 —— 進貨單的金額 / 稅金計算不讀它(REQ-20260902004 §七、REQ-20260902007)。
   * optional 是刻意的:既有 fixture 與 emptyParty 建構子不必為它改一輪。
   */
  taxMode?: 'INCLUSIVE' | 'EXCLUSIVE' | 'EXEMPT' | null;
  /**
   * 廠商才有:主檔的付款條件(REQ-20260907002)。客戶恆為 null。
   * `paymentDays` = 進貨後幾天付款、`closingDay` = 月結帳日。
   * **純顯示用** —— 預計付款日仍然由後端的 `resolveExpectedPaymentDate()` 推算
   * (REQ-20260813003),前端不自己再算一次。運費單的廠商卡片要顯示
   * 「月結 N 天」,不帶回來的話畫面只能造假或留白。
   * optional 是刻意的,理由同上面的 `taxMode`:既有 fixture 與 `emptyParty()`
   * 建構子不必為它改一輪。**兩者都沒設定時一律留空,不猜一個天數**。
   */
  paymentDays?: number | null;
  closingDay?: number | null;
}

export interface PartyLookupResponse {
  items: PartyInfo[];
}

// ──────────────────────────────────────────────────────────────────────────
// 導入訪談紀錄
// ──────────────────────────────────────────────────────────────────────────

/**
 * 一鍵把訪談的「印刷成品規格文字」與「手動導入的印刷商品圖片」帶進單據。
 * 回傳給前端填表單用;圖片本身不在這裡搬,是過帳拋轉代管庫存時才複製。
 */
export interface InterviewImportItem {
  interviewId: number;
  visitedAt: string | null;
  interviewType: string | null;
  contact: string | null;
  /** 訪談內容全文,前端拿去填備註 / 規格 */
  notes: string | null;
  /** 規格候選字串:從訪談內容抓出的行,讓使用者點一下就填進品名 / 規格 */
  specCandidates: string[];
  /** 這筆訪談的圖片附件(僅圖片 mime) */
  images: Array<{
    attachmentId: number;
    uuid: string;
    originalName: string;
    mimeType: string;
    sizeBytes: number;
  }>;
  /** 已經從這筆訪談建過的出貨單號,避免重複建單 */
  existingOrderNos: string[];
}

export interface InterviewImportResponse {
  items: InterviewImportItem[];
}

// ──────────────────────────────────────────────────────────────────────────
// 業務員選單(REQ-20260815002)
// ──────────────────────────────────────────────────────────────────────────

/**
 * 出貨單「業務員」下拉的選項。
 *
 * **為什麼不用既有的 `GET /users`**:那支掛 `@Roles('ADMIN')`,而會計頁是
 * ADMIN / SALES / VIEWER 都進得來的 —— 直接用會讓業務自己開單時下拉是空的
 * (403)。這裡只回 `id + displayName` 兩個欄位,不外洩帳號 / email / 角色,
 * 所以放行的角色範圍與會計頁其他查詢一致。
 */
export interface SalesPersonOption {
  id: number;
  displayName: string;
}

export interface SalesPersonListResponse {
  items: SalesPersonOption[];
}
